import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import PropertyCard from '../components/PropertyCard';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import AreaSelectionModal from '../components/AreaSelectionModal';
import SearchFiltersModal from '../components/SearchFiltersModal';
import Footer from '../components/Footer';
import './Home.css';
import heroImage from '../assets/hero_image.jpg'; // We'll assume this exists or use placeholder

const reviews = [
    {
        name: 'Aarav Mehta',
        avatarColor: '#d97706',
        quote: 'Found a 2 BHK in HSR without the usual back-and-forth. The photos matched the actual flat and the process was much faster than other portals.',
    },
    {
        name: 'Nisha Reddy',
        avatarColor: '#2563eb',
        quote: 'What stood out was the clarity. Rent, deposit, and availability were all upfront, and I could shortlist places in one evening.',
    },
    {
        name: 'Rohan Malhotra',
        avatarColor: '#7c3aed',
        quote: 'The property tours saved me a lot of time. I was relocating from Mumbai and could eliminate weak options before booking visits.',
    },
    {
        name: 'Priya Nair',
        avatarColor: '#db2777',
        quote: 'Clean listings, responsive support, and no nonsense. It felt more curated than a typical rental marketplace.',
    },
    {
        name: 'Karthik Srinivasan',
        avatarColor: '#0f766e',
        quote: 'I appreciated how easy it was to compare homes by area and furnishing. The final property was exactly what the listing promised.',
    },
    {
        name: 'Sneha Kapoor',
        avatarColor: '#b45309',
        quote: 'Most sites feel noisy. This one felt calmer and easier to trust. I found a rental in two weekends instead of dragging it for months.',
    },
    {
        name: 'Vikram Joshi',
        avatarColor: '#1d4ed8',
        quote: 'As an owner, I liked the quality of enquiries. The platform attracted serious tenants and the listing presentation was strong.',
    },
];

const getHomeErrorMessage = (err) => {
    const message = (err?.message || '').toLowerCase();
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

    if (
        isOffline ||
        message.includes('load failed') ||
        message.includes('failed to fetch') ||
        message.includes('network') ||
        message.includes('fetch')
    ) {
        return 'Unable to load properties. Please check your internet connection and try again.';
    }

    return 'Unable to load properties right now. Please try again shortly.';
};

const Home = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modals
    const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Search State
    const [selectedAreas, setSelectedAreas] = useState([]);
    const loopedReviews = [...reviews, ...reviews];

    const navigate = useNavigate();

    // Triggered when Search Bar is clicked
    const openSearchFlow = () => {
        setIsAreaModalOpen(true);
    };

    const handleAreaSelection = (areas) => {
        setSelectedAreas(areas);
        setIsAreaModalOpen(false);
        setIsFilterModalOpen(true);
    };

    const handleFinalSearch = (filters) => {
        setIsFilterModalOpen(false);
        const params = new URLSearchParams();
        if (selectedAreas.length > 0) params.append('localities', selectedAreas.join(','));
        if (filters.listingType) params.append('listingType', filters.listingType);
        if (filters.budget) params.append('budget', filters.budget);
        if (filters.propertyTypes && filters.propertyTypes.length > 0) {
            filters.propertyTypes.forEach(t => params.append('type', t));
        }
        if (filters.bhk && filters.bhk.length > 0) {
            filters.bhk.forEach(b => params.append('bhk', b));
        }
        if (filters.furnishing && filters.furnishing.length > 0) {
            filters.furnishing.forEach(f => params.append('furnishing', f));
        }
        navigate(`/search?${params.toString()}`);
    };

    useEffect(() => {
        fetchProperties();
    }, []);

    const fetchProperties = async () => {
        try {
            const { data, error } = await supabase
                .from('property')
                .select('*, areas(city, name)')
                .eq('status', 'active')
                .order('created_at', { ascending: false })
                .limit(3);

            if (error) throw error;

            const mappedProperties = (data || []).map(p => ({
                ...p,
                price: p.expected_rent,
                images: p.media_url || [],
                bedrooms: p.bhk,
                bathrooms: p.bathrooms,
                area_sqft: p.sqft,
                location: p.areas ? `${p.areas.name}, ${p.areas.city}` : 'Unknown Location'
            }));

            // Show top 3 for Home Page Featured
            setProperties(mappedProperties);

        } catch (err) {
            setError(getHomeErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container light-mode-home">
            <SEO
                title="The Rent Company | Find Your Perfect Place"
                description="Discover premium rental properties tailored to your lifestyle."
                path="/"
            />

            <Navbar transparent={true} />

            <main className="home-main-content">

                {/* Clean Split-Pane Hero Section */}
                <section className="hero-section-clean">
                    <div className="hero-container">

                        <div className="hero-content-left">
                            <h1 className="hero-title">
                                Find Your <span className="text-accent-gold">Perfect</span><br />Place
                            </h1>
                            <p className="hero-description">
                                Discover premium rental properties tailored to your lifestyle. We connect discerning tenants with exclusive urban retreats and suburban sanctuaries.
                            </p>

                            {/* Inline Pill Search Bar */}
                            <div className="hero-search-pill" onClick={openSearchFlow}>
                                <div className="search-segment">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon mustard-icon">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                    <div className="segment-text">
                                        <span>Location</span>
                                    </div>
                                </div>

                                <div className="search-divider"></div>

                                <div className="search-segment">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon mustard-icon">
                                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                                        <line x1="2" y1="10" x2="22" y2="10"></line>
                                    </svg>
                                    <div className="segment-text">
                                        <span>Price Range</span>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                    </div>
                                </div>

                                <div className="search-divider"></div>

                                <div className="search-segment">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon mustard-icon">
                                        <path d="M4 21v-8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8"></path>
                                        <path d="M2 6h20"></path><path d="M12 2v4"></path>
                                    </svg>
                                    <div className="segment-text">
                                        <span>Bedrooms</span>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                    </div>
                                </div>

                                <button className="hero-search-button">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="hero-content-right">
                            <div className="hero-image-wrapper">
                                <img src={heroImage} alt="Premium Modern Home" className="hero-main-image" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80'; }} />

                                {/* Floating Badge */}
                                {/* <div className="floating-badge yellow-badge">
                                    <span className="badge-count">2,500+</span>
                                    <span className="badge-label">PREMIUM LISTINGS</span>
                                </div> */}
                            </div>
                        </div>

                    </div>
                </section>

                <AreaSelectionModal
                    isOpen={isAreaModalOpen}
                    onClose={() => setIsAreaModalOpen(false)}
                    onNext={handleAreaSelection}
                    initialSelectedAreas={selectedAreas}
                />
                <SearchFiltersModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    selectedAreas={selectedAreas}
                    onSearch={handleFinalSearch}
                />

                {/* Featured Listings Section */}
                <section className="featured-section">
                    <div className="section-header">
                        <h2>Featured Listings</h2>
                        <a href="/search" className="view-all-link">View all properties <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></a>
                    </div>

                    {loading ? (
                        <div className="loading">Loading properties...</div>
                    ) : error ? (
                        <div className="error-message">
                            <p>{error}</p>
                            <button type="button" className="retry-button" onClick={fetchProperties}>
                                Try Again
                            </button>
                        </div>
                    ) : properties.length === 0 ? (
                        <div className="empty-state">No properties found.</div>
                    ) : (
                        <div className="clean-properties-grid">
                            {properties.map((property) => (
                                <PropertyCard key={property.id} property={property} />
                            ))}
                        </div>
                    )}
                </section>

                {/* How It Works Section */}
                <section className="how-it-works-section">
                    <h2 className="text-center section-title">How It Works</h2>
                    <p className="text-center section-subtitle">Your journey to a new home simplified into three easy steps.</p>

                    <div className="steps-container">
                        <div className="step-card">
                            <div className="step-icon-wrapper">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </div>
                            <h3>Discover</h3>
                            <p>Browse our curated collection of luxury rentals with detailed high-res photography and virtual tours.</p>
                        </div>
                        <div className="step-card">
                            <div className="step-icon-wrapper">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                    <rect x="10" y="14" width="4" height="4"></rect>
                                </svg>
                            </div>
                            <h3>Visit</h3>
                            <p>Schedule a private viewing at your convenience or explore instantly through our verified property profiles.</p>
                        </div>
                        <div className="step-card">
                            <div className="step-icon-wrapper">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                                </svg>
                            </div>
                            <h3>Move In</h3>
                            <p>Our streamlined digital application process gets you approved and moved in faster than traditional agencies.</p>
                        </div>
                    </div>
                </section>

                {/* Trusted by Professionals */}
                <section className="testimonials-section">
                    <h2 className="text-center text-white section-title">Trusted by <span className="underline-gold">Professionals</span></h2>

                    <div className="testimonials-carousel">
                        <div className="testimonials-track">
                            {loopedReviews.map((review, index) => {
                                const initials = review.name
                                    .split(' ')
                                    .map((part) => part[0])
                                    .join('')
                                    .slice(0, 2)
                                    .toUpperCase();

                                return (
                                    <article className="testimonial-card" key={`${review.name}-${index}`}>
                                        <div className="stars">★★★★★</div>
                                        <p className="quote">"{review.quote}"</p>
                                        <div className="user-info">
                                            <div
                                                className="avatar"
                                                style={{ backgroundColor: review.avatarColor }}
                                            >
                                                {initials}
                                            </div>
                                            <div>
                                                <div className="name">{review.name}</div>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* List Your Property */}
                <section className="cta-section">
                    <div className="cta-container">
                        <div className="cta-text">
                            <h2>Install the app and list now</h2>
                            <p>Post your property through the app, add complete details, and start receiving direct enquiries faster.</p>
                            <div className="cta-buttons">
                                <button className="btn-solid-dark">Install the App</button>
                                <Link to="/how-it-works" className="btn-outline-dark">Our Process</Link>
                            </div>
                        </div>
                        <div className="cta-image">
                            <img src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Modern Interior" />
                        </div>
                    </div>
                </section>

            </main>

            <Footer />
        </div>
    );
};

export default Home;
